import React, { useEffect, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { getStaticMarkdown } from '../../../content/pages_data';

type MarkdownFile = { path: string; name: string; url: string; content: string };
type MarkdownListViewProps = { folder?: string };

function resolvePublicUrl(content: string, publicUrl: string): string {
  if (!content) return content;
  let result = content;
  if (publicUrl) {
    result = result.replace(/__PUBLIC_URL__/g, publicUrl);
    result = result.replace(/(src|href)=("|')(\/(?:page|static)\/[^"']+)\2/g, (m, attr, quote, url) => {
      return `${attr}=${quote}${publicUrl}${url}${quote}`;
    });
  } else {
    result = result.replace(/__PUBLIC_URL__/g, '');
  }
  return result;
}

export default function MarkdownListView({ folder = '' }: MarkdownListViewProps) {
  const publicUrl = process.env.PUBLIC_URL || '';
  const cleanFolder = useMemo(() => (folder || '').replace(/^\/+|\/+$/g, ''), [folder]);

  const [files, setFiles] = useState<MarkdownFile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const staticData = getStaticMarkdown(cleanFolder);
        if (staticData && staticData.length > 0) {
          const resolved = staticData.map(f => ({
            ...f,
            content: resolvePublicUrl(f.content, publicUrl),
          }));
          if (!cancelled) {
            setFiles(resolved);
            setIsLoading(false);
            return;
          }
        }

        const clean = cleanFolder;
        const base = clean.startsWith('page/')
          ? `${publicUrl}/${clean}`
          : `${publicUrl}/page/${clean}`;
        const indexUrl = `${base}/index.json`;

        const res = await fetch(indexUrl);
        if (!res.ok) {
          if (!cancelled) {
            setFiles([]);
            setIsLoading(false);
          }
          return;
        }
        const data = await res.json();
        const list: string[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.files)
            ? data.files
            : [];
        const items: MarkdownFile[] = await Promise.all(
          list.map(async (fname) => {
            const url = `${base}/${fname}`;
            const r = await fetch(url);
            const text = await r.text();
            const name = fname.replace(/\.md$/, '');
            return {
              path: fname,
              name,
              url,
              content: resolvePublicUrl(text, publicUrl),
            };
          })
        );
        if (!cancelled) {
          setFiles(items);
          setIsLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setFiles([]);
          setIsLoading(false);
        }
      }
    };

    setIsLoading(true);
    const t = setTimeout(() => {
      load();
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [cleanFolder, publicUrl]);

  return (
    <section className="max-w-7xl m-10 mx-auto px-4">
      <ul className="mt-4 space-y-6 list-none">
        {files.map(f => (
          <li key={f.path} className="prose prose-sm max-w-none p-4 rounded-md bg-white text-slate-700 prose-headings:text-[#3A3168] prose-p:text-slate-700 prose-strong:text-slate-900 prose-code:text-slate-800">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{f.content}</ReactMarkdown>
          </li>
        ))}
        {isLoading && (
          Array.from({ length: 4 }).map((_, i) => (
            <li key={`skeleton-${i}`} className="p-4 rounded-md bg-white">
              <div className="animate-pulse">
                <div className="h-20 bg-slate-200 rounded w-full mb-3"></div>
                <div className="h-10 bg-slate-200 rounded w-full mb-3"></div>
                <div className="flex gap-2">
                  <div className="h-10 bg-slate-200 rounded w-2/6 mb-3"></div>
                  <div className="h-10 bg-slate-200 rounded w-2/6 mb-2"></div>
                </div>
              </div>
            </li>
          ))
        )}
        {!isLoading && files.length === 0 && (
          <li className="text-slate-500">Nenhum arquivo Markdown encontrado.</li>
        )}
      </ul>
    </section>
  );
}
